// core/skillLoader.js
// Lazy loading for skill modules

import { skillRegistry } from './skillRegistry.js';

/**
 * Map of skill IDs to their module loader functions
 * Each loader returns a dynamic import promise
 */
const skillModules = {
    mining: () => import('../skills/mining/index.js'),
    woodcutting: () => import('../skills/woodcutting/index.js'),
    fishing: () => import('../skills/fishing/index.js'),
    pilfering: () => import('../skills/pilfering/index.js'),
    farming: () => import('../skills/farming/index.js'),
    architecting: () => import('../skills/architecting/index.js'),
    crafting: () => import('../skills/crafting/index.js'),
    smithing: () => import('../skills/smithing/index.js'),
    pyre: () => import('../skills/pyre/index.js'),
    cooking: () => import('../skills/cooking/index.js'),
    woodworking: () => import('../skills/woodworking/index.js'),
    glyphbinding: () => import('../skills/glyphbinding/index.js'),
    alchemy: () => import('../skills/alchemy/index.js'),
    gatherer: () => import('../skills/gatherer/index.js'),
    husbandry: () => import('../skills/husbandry/index.js'),
    bounty: () => import('../skills/bounty/index.js'),
    admin: () => import('../skills/admin/index.js'),
    combat: () => import('../skills/combat/index.js'),
};

/** Track which skills have been loaded */
const loadedSkills = new Set();

/** Track loading promises to prevent duplicate loads */
const loadingPromises = new Map();

/**
 * Load a skill module dynamically
 * @param {string} skillId - Skill identifier
 * @returns {Promise<Object|null>} - The loaded module or null if not found
 */
export async function loadSkill(skillId) {
    // Already loaded
    if (loadedSkills.has(skillId)) {
        return skillRegistry.get(skillId);
    }

    // Already loading - return existing promise
    if (loadingPromises.has(skillId)) {
        return loadingPromises.get(skillId);
    }

    // Check if loader exists
    const loader = skillModules[skillId];
    if (!loader) {
        console.warn(`[SkillLoader] No module loader for skill: ${skillId}`);
        return null;
    }

    // Create loading promise
    const loadPromise = (async () => {
        try {
            const module = await loader();

            // The module should self-register with skillRegistry
            // But we mark it as loaded here
            loadedSkills.add(skillId);

            // Return the registered config (may differ from raw module)
            return skillRegistry.get(skillId) || module.default;
        } catch (error) {
            console.error(`[SkillLoader] Failed to load skill '${skillId}':`, error);
            return null;
        } finally {
            loadingPromises.delete(skillId);
        }
    })();

    loadingPromises.set(skillId, loadPromise);
    return loadPromise;
}

/**
 * Preload multiple skills
 * @param {string[]} skillIds - Array of skill IDs to preload
 * @returns {Promise<void>}
 */
export async function preloadSkills(skillIds) {
    await Promise.all(skillIds.map(id => loadSkill(id)));
}

/**
 * Check if a skill module is loaded
 * @param {string} skillId
 * @returns {boolean}
 */
export function isSkillLoaded(skillId) {
    return loadedSkills.has(skillId);
}

/**
 * Check if a skill module is currently loading
 * @param {string} skillId
 * @returns {boolean}
 */
export function isSkillLoading(skillId) {
    return loadingPromises.has(skillId);
}

/**
 * Get all available skill IDs (registered loaders)
 * @returns {string[]}
 */
export function getAvailableSkills() {
    return Object.keys(skillModules);
}

/**
 * Check if a skill has a module loader registered
 * @param {string} skillId
 * @returns {boolean}
 */
export function hasSkillModule(skillId) {
    return skillId in skillModules;
}

/**
 * Register a new skill module loader (for dynamic skill addition)
 * @param {string} skillId
 * @param {Function} loader - Function returning dynamic import promise
 */
export function registerSkillModule(skillId, loader) {
    if (skillModules[skillId]) {
        console.warn(`[SkillLoader] Overwriting module loader for: ${skillId}`);
    }
    skillModules[skillId] = loader;
}

/**
 * Clear loaded state for a skill (primarily for testing)
 * @param {string} skillId
 */
export function unloadSkill(skillId) {
    loadedSkills.delete(skillId);
    loadingPromises.delete(skillId);
}

/**
 * Clear all loaded skills (primarily for testing)
 */
export function clearLoadedSkills() {
    loadedSkills.clear();
    loadingPromises.clear();
}

export default {
    loadSkill,
    preloadSkills,
    isSkillLoaded,
    isSkillLoading,
    getAvailableSkills,
    hasSkillModule,
    registerSkillModule,
    unloadSkill,
    clearLoadedSkills,
};
